import React from "react";
import {
  Avatar,
  Box,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  MenuItem,
  TextField,
} from "@mui/material";
import { SxProps } from "@mui/material/styles";
import AuthTypes from "../../Interfaces/auth-interface";
import { useTranslation } from "react-i18next";
import { Icons } from "../../Constants/Icons";
import { useDir } from "../../Contexts/DirectionContext";
import { mobileLayoutMedia } from "../../Hooks/useMobileLayout";
import {
  SearchableSelect,
  SearchMenuTone,
  SearchOption,
} from "./SearchableSelect";

const CustomInput = ({
  name,
  label,
  type,
  autoFocus,
  half,
  third,
  quarter,
  handleChange,
  onKeyDown,
  handleShowPassword,
  selected,
  select,
  value,
  defaultValue,
  placeholder,
  disabled,
  helperText,
  error,
  className,
  multiline,
  rows,
  minRows,
  maxRows,
  required,
  ref,
  InputProps,
  inputComponent,
  countries,
  id,
  endComponent,
  small,
  activeItemsDisabled,
  onBlur,
  autoComplete,
  SelectProps,
  fieldWidth,
  gridSx,
  labelAction,
  searchable,
  pinnedOptions,
  menuTone,
  loading,
}: Partial<AuthTypes["inputType"]> & {
  onBlur?: () => void;
  /** Sits on the label's row, after the text, so the field keeps its width. */
  labelAction?: React.ReactNode;
  /** Rides on the field's own grid cell, for a caller that needs it to take
   *  a different share of the row at one width. */
  gridSx?: SxProps;
  /** With `select`, lets the list be narrowed by typing. Only the picked
   *  option's value is ever sent to `handleChange`, never the typed text. */
  searchable?: boolean;
  /** Action rows held at the foot of a searchable list, whatever is typed. */
  pinnedOptions?: SearchOption[];
  menuTone?: SearchMenuTone;
  /** A searchable list still loading. */
  loading?: boolean;
}) => {
  const { t } = useTranslation(["main"]);
  const { Globals } = useDir();

  return (
    <Grid
      item
      xs={12}
      sm={quarter || third ? 6 : half ? 6 : 12}
      md={quarter ? 3 : third ? 4 : half ? 6 : 12}
      sx={{ width: '100%', ...gridSx }}
    >
      <Box
        sx={{
          width: fieldWidth || '100%',
          // A field narrowed to sit better beside its neighbour has no
          // neighbour once the form stacks, so it takes the row back.
          [mobileLayoutMedia]: { width: '100%' },
          display: 'flex',
          alignItems: 'start',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: 'var(--field-label-gap, 8px)',
        }}
      >
        {/* Only wrapped when there is something to sit beside the label: a
            caller that hides the label (`.MuiInputLabel-root { display:none }`)
            would otherwise be left with an empty flex item, and the column's
            own gap above the field. */}
        {labelAction ? (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '4px',
            }}
          >
            <InputLabel
              required={required}
              htmlFor={id}
              sx={{ padding: '0 0 0 5px' }}
            >
              {t(label!)}
            </InputLabel>
            <Box sx={{ height: 0, display: 'flex', alignItems: 'center' }}>
              {labelAction}
            </Box>
          </Box>
        ) : (
          <InputLabel
            required={required}
            htmlFor={id}
            sx={{ padding: '0 0 0 5px' }}
          >
            {t(label!)}
          </InputLabel>
        )}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
          }}
        >
          {select && searchable ? (
            <SearchableSelect
              id={id}
              name={name}
              value={value}
              options={(selected || []) as SearchOption[]}
              pinned={pinnedOptions}
              // Handlers here read `e.target`, so the pick is handed over in
              // the shape the plain dropdown's change event had.
              onChange={(picked) =>
                handleChange?.({ target: { name, value: picked } } as any)
              }
              onOpen={SelectProps?.onOpen as (() => void) | undefined}
              loading={loading}
              disabled={disabled}
              placeholder={placeholder}
              error={error}
              helperText={helperText}
              small={small}
              paperSx={(SelectProps?.MenuProps?.PaperProps as any)?.sx}
              tone={menuTone}
            />
          ) : (
          <TextField
            defaultValue={defaultValue}
            onKeyDown={onKeyDown}
            id={id}
            ref={ref}
            size={small ? 'small' : 'medium'}
            multiline={multiline}
            rows={rows}
            minRows={minRows}
            maxRows={maxRows}
            dir={document.dir}
            error={error}
            name={name}
            helperText={helperText}
            placeholder={placeholder}
            disabled={disabled}
            onChange={handleChange}
            variant='outlined'
            required={required}
            fullWidth
            autoFocus={autoFocus}
            type={type}
            autoComplete={autoComplete === undefined ? undefined : autoComplete ? 'on' : 'off'}
            select={select}
            SelectProps={SelectProps}
            value={value}
            className={className}
            sx={{
              '.MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '16px 14px',
              },
            }}
            InputProps={
              name === 'password' || name === 'confirmPassword'
                ? {
                    endAdornment: (
                      <InputAdornment position={Globals.start as any}>
                        <IconButton onClick={handleShowPassword}>
                          {type === 'password' ? (
                            <Icons.Hide />
                          ) : (
                            <Icons.Show />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                    startAdornment: (
                      <InputAdornment position={Globals.end as any}>
                        <Icons.Lock />
                      </InputAdornment>
                    ),
                  }
                : InputProps
            }
            onBlur={onBlur}
          >
            {selected?.map(
              (option: AuthTypes['sectionInputType'], index: number) => (
                <MenuItem
                  key={index}
                  value={option.value as any}
                  disabled={activeItemsDisabled ? false : option?.disabled}
                  sx={{ gap: '6px', color: option?.color }}
                >
                  {option?.icon && (
                    <ListItemIcon>
                      <option.icon />
                    </ListItemIcon>
                  )}
                  {option?.image && (
                    <Avatar
                      src={option?.src}
                      sx={{ height: 18, width: 18, background: '#fff' }}
                    />
                  )}
                  {/* Separators off: an option's label can be data rather
                      than a key — a geo name carrying ":" would otherwise be
                      read as a namespace and rendered as its own tail. */}
                  {t(option.label, { nsSeparator: false, keySeparator: false })}
                </MenuItem>
              )
            )}
            {countries?.map((option: any, index: number) => (
              <MenuItem key={index} value={option.name}>
                {option?.flag && (
                  <ListItemIcon>
                    <img src={option.flag} alt={option.name} />
                  </ListItemIcon>
                )}
                {t(option.name)}
              </MenuItem>
            ))}
          </TextField>
          )}
          {endComponent && endComponent}
        </Box>
      </Box>
    </Grid>
  );
};

export default CustomInput;
